import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material"
import { DeleteOutlined, EditOutlined, MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import type { FC } from "react"
import { useRef, useState } from "react"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import type { CompetitorProduct } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface CompetitorRowActionsProps {
  row: CellContext<CompetitorProduct, unknown>["row"]
  onEdit?: (product: CompetitorProduct) => void
  onDelete?: (competitorProductId: string, productName: string) => Promise<boolean>
}

const CompetitorRowActions: FC<CompetitorRowActionsProps> = ({ row, onEdit, onDelete }) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const handleMenuOpen = () => setIsMenuOpen(true)
  const handleMenuClose = () => setIsMenuOpen(false)

  const handleEdit = () => {
    handleMenuClose()
    onEdit?.(row.original)
  }

  const handleRemoveClick = () => {
    handleMenuClose()
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    const success = await onDelete?.(row.original.competitorProductId, row.original.productName)
    if (success) {
      setIsDeleteDialogOpen(false)
    } else {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: "Failed to remove competitor product. Please try again.",
      })
    }
  }

  return (
    <Box className='flex items-center justify-end' ref={anchorElRef}>
      <IconButton onClick={handleMenuOpen} data-test='row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <Menu anchorEl={anchorElRef.current} open={isMenuOpen} onClose={handleMenuClose} data-test='row-actions-menu'>
        <MenuItem data-test='edit-menu-item' onClick={handleEdit}>
          <ListItemIcon>
            <EditOutlined />
          </ListItemIcon>
          <ListItemText primary='Edit Product Information' />
        </MenuItem>
        <MenuItem data-test='remove-menu-item' onClick={handleRemoveClick}>
          <ListItemIcon>
            <DeleteOutlined />
          </ListItemIcon>
          <ListItemText primary='Remove Product' />
        </MenuItem>
      </Menu>
      {isDeleteDialogOpen && (
        <ConfirmationDialog
          title={`Are you sure you want to remove ${row.original.productName}?`}
          content='This action cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          isWarning
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </Box>
  )
}

export default CompetitorRowActions
